import React, { useContext } from "react";
import type { Style } from "mapbox-gl";
import queryString from "query-string";
import { useLocation } from "react-router-dom";
import { getPath } from "common/routing";
import { track } from "../../utils/tracking";
import { getCurrentURL } from "../../utils/url";
import { Icon } from "../Common/Icons/LucideIcons";
import {
  ActiveFloodLayerBadge,
  Container,
  Expand,
  SquareLink,
} from "./__styles__/Preview";
import { LayeredMap } from "./LayeredMap";
import { FirmLayerConfig } from "./LayeredMap/firmLayer";
import { LayerContext } from "./layers";
import { DEFAULT_POINT_ZOOM } from "./utils/viewportHook";

interface Props {
  height: string;
  width: string;
  latitude: number;
  longitude: number;
  zoom?: number;
  account: {
    id: string;
    defaultBaseMap: {
      mapboxStyle: Style;
    };
    firms: FirmLayerConfig["firms"];
  };
}

export default ({
  height,
  width,
  latitude,
  longitude,
  zoom = DEFAULT_POINT_ZOOM,
  account,
}: Props) => {
  const { visibleFIRM, visibleRaster } = useContext(LayerContext);
  const prevLocation = getCurrentURL(useLocation());

  const layers = {
    parcels: { value: { point: { latitude, longitude } } },
    firms: { firms: account.firms },
    marker: { value: { latitude, longitude } },
  };

  const trackExpandAction = () => {
    track("Map Expanded", {
      source: "propertyProfile",
    });
  };

  const activeFloodLayerName =
    visibleFIRM()?.name ?? visibleRaster()?.layerName;

  return (
    <Container>
      <LayeredMap
        width={width}
        height={height}
        initialViewport={{
          zoom,
          latitude,
          longitude,
        }}
        baseMapStyle={account.defaultBaseMap.mapboxStyle}
        account={account}
        layers={layers}
      />

      {activeFloodLayerName && (
        <ActiveFloodLayerBadge style={{ marginRight: "-8px" }}>
          {activeFloodLayerName}
        </ActiveFloodLayerBadge>
      )}
      <Expand>
        <SquareLink
          onClick={trackExpandAction}
          to={{
            pathname: getPath("explore"),
            state: { prevLocation },
            search: queryString.stringify({
              lat: latitude,
              lng: longitude,
              firmId: visibleFIRM()?.id,
            }),
          }}
          data-testid="expand"
        >
          <Icon iconName="expand" color="contentPrimary" size="16" />
        </SquareLink>
      </Expand>
    </Container>
  );
};
